import type { ControlMessage, Envelope, JoinMessage, RelayMessage } from '@tellvo/setup-protocol';
import { isEnvelope } from '@tellvo/setup-protocol';

export type RelayEvent =
  | { type: 'relay'; message: RelayMessage }
  | { type: 'envelope'; envelope: Envelope }
  | { type: 'closed' };

/** One WebSocket to the relay with typed events. */
export class RelayClient {
  private socket: WebSocket | null = null;
  private listeners: ((event: RelayEvent) => void)[] = [];

  constructor(private readonly url: string) {}

  onEvent(listener: (event: RelayEvent) => void): void {
    this.listeners.push(listener);
  }

  connect(join: JoinMessage): Promise<void> {
    return new Promise((resolve, reject) => {
      const socket = new WebSocket(this.url);
      this.socket = socket;
      socket.onopen = () => {
        socket.send(JSON.stringify(join));
        resolve();
      };
      socket.onerror = () => reject(new Error('relay unreachable'));
      socket.onclose = () => this.emit({ type: 'closed' });
      socket.onmessage = (event) => {
        let parsed: unknown;
        try {
          parsed = JSON.parse(String(event.data));
        } catch {
          return;
        }
        if (isEnvelope(parsed)) this.emit({ type: 'envelope', envelope: parsed });
        else if (typeof parsed === 'object' && parsed && 't' in parsed) this.emit({ type: 'relay', message: parsed as RelayMessage });
      };
    });
  }

  send(message: Envelope | JoinMessage | ControlMessage): void {
    // A bye on unload may race a socket the relay already closed; sending then only logs an error.
    if (this.socket?.readyState === WebSocket.OPEN) this.socket.send(JSON.stringify(message));
  }

  close(): void {
    this.socket?.close();
    this.socket = null;
  }

  private emit(event: RelayEvent): void {
    for (const listener of this.listeners) listener(event);
  }
}
